import * as PIXI from 'pixi.js';

import type { UpdateFlowMixinSettings } from '../../updateFlowMixin';
import { ReactiveGraphic } from './ReactiveGraphic';
import {
    AlphaAttribute,
    ColorAttribute,
    NumberAttribute,
    StringAttribute,
    getAttributesToSerialize,
} from '../../attributes';
import type { LoadSettings } from '../../serializableMixin';
import type { RawAssetData } from '../../../video-data/types';
import { Config } from '../../../video-data/config';
import { getCircleOffsetByRadiusAndStrokeWidth } from '../../CircleUtils';
import { int2rgba } from '../../../libs/videoData';
import { createLinearGradient } from '../../../libs/gradient';
import { GradientPoint } from '../../GradientPoint';
import type { GradientStop } from '../../GradientPoint';
import type { DeepReadonlyObject, DestroyOptions } from '../../../types';
import { comparer, observable, reaction, runInAction } from '../../reactivity';

type CircleGraphicState = DeepReadonlyObject<
    Pick<
        CircleGraphic,
        | 'radius'
        | 'positionX'
        | 'positionY'
        | 'strokeColor'
        | 'strokeAlpha'
        | 'strokeWidth'
        | 'fillColor'
        | 'fillAlpha'
        | 'fillType'
    > & { nonAnimatedRadius: number; gradientStart: GradientStop; gradientEnd: GradientStop }
>;

/**
 * Draws a colored bounding box around the entire text(Metrics)
 *
 */
export class CircleGraphic extends ReactiveGraphic {
    override _classType = 'CircleGraphic';
    override _name = Config.CIRCLE_GRAPHIC;

    @NumberAttribute({ min: 0 })
    @observable
    accessor radius = 0;
    @NumberAttribute({ animatable: false })
    @observable
    accessor positionX = 0;
    @NumberAttribute({ animatable: false })
    @observable
    accessor positionY = 0;
    @ColorAttribute()
    @observable
    accessor strokeColor = 16777215;
    @AlphaAttribute()
    @observable
    accessor strokeAlpha = 1;
    @NumberAttribute({ min: 0, animatable: false })
    @observable
    accessor strokeWidth = 0;
    @ColorAttribute()
    @observable
    accessor fillColor = 16777215;
    @AlphaAttribute()
    @observable
    accessor fillAlpha = 1;
    @StringAttribute({ choices: ['solid', 'linear'], animatable: false })
    @observable
    accessor fillType: 'solid' | 'linear' = 'solid';

    gradientStartPoint = new GradientPoint({ parent: this, x: 0, y: 0.5, color: 0xff0000 });
    gradientEndPoint = new GradientPoint({ parent: this, x: 1, y: 0.5, color: 0x0000ff });

    @observable accessor #nonAnimatedRadius: number = 0;
    #canvas: HTMLCanvasElement | null = null;
    #gradientTexture: PIXI.Texture | null = null;
    #gradientTextureKey: string | null = null;
    #renderedGraphicsState: CircleGraphicState | null = null;

    constructor(settings: UpdateFlowMixinSettings) {
        super(settings);
        this.addReaction(
            reaction(
                () => this.#graphicsState(),
                (state) => this.#applyGraphicsState(state),
                { name: 'CircleGraphic.graphicsState' },
            ),
        );
    }

    #graphicsState(): CircleGraphicState {
        return {
            radius: this.radius,
            nonAnimatedRadius: this.#nonAnimatedRadius,
            positionX: this.positionX,
            positionY: this.positionY,
            strokeColor: this.strokeColor,
            strokeAlpha: this.strokeAlpha,
            strokeWidth: this.strokeWidth,
            fillColor: this.fillColor,
            fillAlpha: this.fillAlpha,
            fillType: this.fillType,
            gradientStart: this.gradientStartPoint._toStop(),
            gradientEnd: this.gradientEndPoint._toStop(),
        };
    }

    #applyGraphicsState(state: CircleGraphicState) {
        if (comparer.structural(state, this.#renderedGraphicsState)) {
            return;
        }
        this.clear();
        this.lineStyle(state.strokeWidth, state.strokeColor, state.strokeAlpha);

        const fillColor = state.fillType === 'solid' ? state.fillColor : 0xffffff;
        const fillAlpha = state.fillType === 'solid' ? state.fillAlpha : 1;

        const offset = getCircleOffsetByRadiusAndStrokeWidth(
            state.nonAnimatedRadius,
            state.strokeWidth,
        );
        const circleCenterX = state.positionX + offset;
        const circleCenterY = state.positionY + offset;

        if (state.fillType === 'linear') {
            const texture = this.#createGradientTexture(
                state.gradientStart,
                state.gradientEnd,
                state.radius,
            );
            if (texture) {
                // beginTextureFill inverts the matrix before applying it, so pass
                // the positive offset (PIXI inverts to -(center-radius)) to align
                // the texture's top-left with the circle's bounding box.
                const matrix = new PIXI.Matrix();
                matrix.translate(circleCenterX - state.radius, circleCenterY - state.radius);
                this.beginTextureFill({ texture, matrix });
            } else {
                this.beginFill(fillColor, fillAlpha);
            }
        } else {
            this.beginFill(fillColor, fillAlpha);
        }

        this.drawCircle(circleCenterX, circleCenterY, state.radius);
        this.endFill();
        this.#renderedGraphicsState = state;
    }

    #createGradientTexture(start: GradientStop, end: GradientStop, radius: number) {
        if (!this.#canvas) {
            this.#canvas = document.createElement('canvas');
        }
        const shapeDiameter = Math.max(1, Math.round(radius * 2));

        const key = [
            shapeDiameter,
            start.x,
            start.y,
            start.color,
            start.alpha,
            end.x,
            end.y,
            end.color,
            end.alpha,
        ].join(':');
        if (this.#gradientTexture && this.#gradientTextureKey === key) {
            return this.#gradientTexture;
        }

        // +1 px padding: UV at the bounding-box edge becomes diameter/(diameter+1) < 1.0,
        // preventing wrap-around artifacts regardless of the texture's wrapMode.
        const canvasSize = shapeDiameter + 1;
        this.#canvas.width = canvasSize;
        this.#canvas.height = canvasSize;

        const context = this.#canvas.getContext('2d');
        if (!context) return null;

        context.clearRect(0, 0, canvasSize, canvasSize);

        // The gradient points are normalized to the circle's bounding box (the diameter square).
        const gradient = createLinearGradient(context, {
            startX: start.x * shapeDiameter,
            startY: start.y * shapeDiameter,
            endX: end.x * shapeDiameter,
            endY: end.y * shapeDiameter,
            startStop: int2rgba(start.color, start.alpha),
            endStop: int2rgba(end.color, end.alpha),
        });

        context.fillStyle = gradient;
        context.fillRect(0, 0, canvasSize, canvasSize);

        if (this.#gradientTexture) {
            this.#gradientTexture.destroy(true);
        }
        this.#gradientTexture = PIXI.Texture.from(this.#canvas);
        this.#gradientTextureKey = key;
        return this.#gradientTexture;
    }

    override load(dataObj: RawAssetData, settings?: LoadSettings) {
        runInAction(() => {
            super.load(dataObj, settings);
            this.#nonAnimatedRadius = this.radius;
        });
    }

    protected override _calculateBounds() {
        this.#applyGraphicsState(this.#graphicsState());
        super._calculateBounds();
    }

    override get propertiesToSerialize(): string[] {
        return getAttributesToSerialize(this).concat(['gradientStartPoint', 'gradientEndPoint']);
    }

    override destroy(options?: DestroyOptions) {
        if (this.#gradientTexture) {
            this.#gradientTexture.destroy(true);
            this.#gradientTexture = null;
            this.#gradientTextureKey = null;
        }
        super.destroy(options);
    }
}
